import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Brain, Sparkles, TrendingUp, AlertTriangle, Lightbulb, MessageSquare } from 'lucide-react';
import { SOMAChatInterface } from './SOMAChatInterface';
import { getInsights as getBrowserInsights } from '../../../lib/browser-compat';

interface Insight {
    id: string;
    type: 'recommendation' | 'warning' | 'opportunity' | 'prediction';
    title: string;
    description: string;
    confidence: number;
    impact: 'low' | 'medium' | 'high';
    actionable: boolean;
}

export const HiveMindInsights: React.FC = () => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [showSomaChat, setShowSomaChat] = useState(false);
    const [somaQuery, setSomaQuery] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [scope, setScope] = useState('bee-execution');
    const [metrics, setMetrics] = useState<{ totalTraces: number } | null>(null);
    const [insights, setInsights] = useState<Insight[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);

    const humanizeHiveMindType = (t: string): Insight['type'] => {
        switch (t) {
            case 'anomaly':
                return 'warning';
            case 'optimization':
                return 'opportunity';
            case 'prediction':
                return 'prediction';
            case 'pattern':
            case 'correlation':
            default:
                return 'recommendation';
        }
    };

    const impactFromPriority = (p: string): Insight['impact'] => {
        switch (p) {
            case 'critical':
            case 'high':
                return 'high';
            case 'medium':
                return 'medium';
            default:
                return 'low';
        }
    };

    const formatDescription = (data: unknown): string => {
        try {
            const str = typeof data === 'string' ? data : JSON.stringify(data);
            if (!str) return 'No details provided.';
            return str.length > 240 ? `${str.slice(0, 240)}…` : str;
        } catch {
            return 'No details provided.';
        }
    };

    const loadHiveMind = useCallback(async () => {
        setIsGenerating(true);
        setLoadError(null);

        try {
            const [metricsRes, insightsRes] = await Promise.all([
                fetch('/api/hivemind/v2/metrics', { method: 'GET' }),
                fetch(`/api/hivemind/v2/insights?scope=${encodeURIComponent(scope)}`, {
                    method: 'GET',
                }),
            ]);

            if (!metricsRes.ok) {
                throw new Error(`HiveMind metrics request failed (${metricsRes.status})`);
            }
            if (!insightsRes.ok) {
                throw new Error(`HiveMind insights request failed (${insightsRes.status})`);
            }

            const metricsJson = (await metricsRes.json()) as { success?: boolean; metrics?: any };
            const insightsJson = (await insightsRes.json()) as {
                success?: boolean;
                insights?: Array<{
                    id: string;
                    type: string;
                    confidence: number;
                    data: unknown;
                    actionable: boolean;
                    priority: string;
                }>;
            };

            setMetrics({
                totalTraces:
                    typeof metricsJson?.metrics?.totalTraces === 'number'
                        ? metricsJson.metrics.totalTraces
                        : 0,
            });

            const normalized = (insightsJson?.insights || []).map(i => {
                const confidencePct = Math.max(
                    0,
                    Math.min(100, Math.round((i.confidence || 0) * 100))
                );
                const uiType = humanizeHiveMindType(i.type);
                const title =
                    i.type === 'pattern'
                        ? 'Pattern detected'
                        : i.type === 'anomaly'
                          ? 'Anomaly detected'
                          : i.type === 'optimization'
                            ? 'Optimization suggestion'
                            : i.type === 'prediction'
                              ? 'Prediction'
                              : 'Insight';

                return {
                    id: i.id,
                    type: uiType,
                    title,
                    description: formatDescription(i.data),
                    confidence: confidencePct,
                    impact: impactFromPriority(i.priority),
                    actionable: Boolean(i.actionable),
                } satisfies Insight;
            });

            setInsights(normalized);
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to load HiveMind insights';
            setLoadError(message);

            // Fallback: browser-compatible (sessionStorage-backed) stub insights
            const fallback = await getBrowserInsights(scope);
            const normalizedFallback: Insight[] = (fallback || []).map((i: any, idx: number) => ({
                id: `fallback-${idx}`,
                type: 'recommendation',
                title: i?.pattern ? `Pattern: ${i.pattern}` : 'Insight',
                description:
                    typeof i?.frequency === 'number'
                        ? `Frequency: ${(i.frequency * 100).toFixed(0)}%`
                        : 'No details provided.',
                confidence: 50,
                impact: 'low',
                actionable: false,
            }));
            setInsights(normalizedFallback);
        } finally {
            setIsGenerating(false);
        }
    }, [scope]);

    useEffect(() => {
        void loadHiveMind();
    }, [loadHiveMind]);

    const handleSomaQuery = (query: string) => {
        if (!query.trim()) return;

        const narrativeSuggestions = insights
            .map(
                insight =>
                    `Based on ${insight.title}: ${insight.description} (${insight.confidence}% confidence)`
            )
            .join('\n\n');

        const chartSuggestions = [
            'Progress Timeline Chart - Show project milestones and completion rates',
            'Resource Utilization Chart - Display team capacity and bottlenecks',
            'Risk Assessment Matrix - Visualize impact vs probability of identified risks',
            'Engagement Trend Chart - Track client interaction patterns over time',
        ];

        const somaPrompt = `${query}\n\nNarrative Context:\n${narrativeSuggestions}\n\nSuggested Charts:\n${chartSuggestions.join('\n')}`;

        setShowSomaChat(true);
        setSomaQuery(somaPrompt);
        setInputValue('');
    };

    const getInsightIcon = (type: string) => {
        switch (type) {
            case 'recommendation':
                return <Lightbulb className="w-5 h-5 text-blue-600" />;
            case 'warning':
                return <AlertTriangle className="w-5 h-5 text-red-600" />;
            case 'opportunity':
                return <TrendingUp className="w-5 h-5 text-[#8A9150]" />;
            case 'prediction':
                return <Sparkles className="w-5 h-5 text-purple-600" />;
            default:
                return <Brain className="w-5 h-5 text-gray-600" />;
        }
    };

    const getInsightColor = (type: string) => {
        switch (type) {
            case 'recommendation':
                return 'bg-blue-50 border-blue-200';
            case 'warning':
                return 'bg-red-50 border-red-200';
            case 'opportunity':
                return 'bg-green-50 border-green-200';
            case 'prediction':
                return 'bg-purple-50 border-purple-200';
            default:
                return 'bg-gray-50 border-gray-200';
        }
    };

    const getImpactColor = (impact: string) => {
        switch (impact) {
            case 'high':
                return 'text-red-600 bg-red-100 border-red-200';
            case 'medium':
                return 'text-yellow-600 bg-yellow-100 border-yellow-200';
            case 'low':
                return 'text-gray-600 bg-gray-100 border-gray-200';
            default:
                return 'text-gray-600 bg-gray-100 border-gray-200';
        }
    };

    return (
        <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
            <div className="flex items-center justify-between mb-6">
                <div className="flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Brain className="w-5 h-5 text-[#4267A1] mr-2" />
                        HiveMind AI Insights
                    </h3>
                    <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                        <span>Scope:</span>
                        <select
                            value={scope}
                            onChange={e => setScope(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 bg-white"
                        >
                            <option value="bee-execution">bee-execution</option>
                            <option value="command-execution">command-execution</option>
                            <option value="coordination">coordination</option>
                            <option value="session-management">session-management</option>
                            <option value="error-tracking">error-tracking</option>
                        </select>
                        <span>•</span>
                        <span>Total traces: {metrics?.totalTraces ?? 0}</span>
                        {loadError && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
                                Fallback
                            </span>
                        )}
                    </div>
                </div>

                <button
                    onClick={loadHiveMind}
                    disabled={isGenerating}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                    <Sparkles className="w-4 h-4" />
                    <span>{isGenerating ? 'Loading...' : 'Refresh'}</span>
                </button>
            </div>

            {loadError && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                    {loadError} (showing fallback insights)
                </div>
            )}

            <div className="space-y-4">
                {insights.length === 0 ? (
                    <div className="p-4 border rounded-lg bg-gray-50 border-gray-200 text-sm text-gray-600">
                        No insights yet for <span className="font-mono">{scope}</span>. Record some
                        traces via
                        <span className="font-mono"> POST /api/hivemind/v2/trace</span> and refresh.
                    </div>
                ) : (
                    insights.map(insight => (
                        <div
                            key={insight.id}
                            className={`p-4 border rounded-lg ${getInsightColor(insight.type)}`}
                        >
                            <div className="flex items-start space-x-3">
                                {getInsightIcon(insight.type)}
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="font-semibold text-gray-900">
                                            {insight.title}
                                        </h4>
                                        <div className="flex items-center space-x-2">
                                            <span
                                                className={`px-2 py-1 rounded-full text-xs font-medium border ${getImpactColor(insight.impact)}`}
                                            >
                                                {insight.impact.toUpperCase()} IMPACT
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {insight.confidence}% confidence
                                            </span>
                                        </div>
                                    </div>

                                    <p className="text-gray-700 mb-3">{insight.description}</p>

                                    {insight.actionable && (
                                        <div className="flex items-center space-x-2">
                                            <button className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 transition-colors">
                                                Take Action
                                            </button>
                                            <button className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 transition-colors">
                                                Learn More
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-[#4267A1]" />
                    <h4 className="font-medium text-gray-900">Ask Soma</h4>
                </div>
                <div className="flex space-x-2">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleSomaQuery(inputValue)}
                        placeholder="Ask Soma for narrative + chart suggestions..."
                        className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#4267A1] focus:border-transparent"
                    />
                    <button
                        onClick={() => handleSomaQuery(inputValue)}
                        className="px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200"
                    >
                        Ask
                    </button>
                </div>
            </div>

            <div className="mt-4 text-xs text-gray-500">
                <p>• Insights loaded from /api/hivemind/v2/insights (with browser fallback)</p>
                <p>
                    • This panel is intentionally “boring first”: explicit API calls, no monkey
                    patching
                </p>
            </div>

            {showSomaChat && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg w-4/5 h-4/5 max-w-4xl">
                        <SOMAChatInterface
                            onClose={() => setShowSomaChat(false)}
                            initialQuery={somaQuery}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
